import { useContext, useMemo } from "react";

import { IconHouse, IconInternalUsers, IconSubsVendors, IconXCircle } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTLink } from "@btnet/core-components/BTLink";
import { BTPopover } from "@btnet/core-components/BTPopover";
import type { ApprovalsUser } from "@btnet/services-btnet/handlers/models/approvalsUser";
import { BTLoginTypes } from "@btnet/services-btnet/handlers/models/bTLoginTypes";
import { SignatureRequestUserStatus } from "@btnet/services-btnet/handlers/models/signatureRequestUserStatus";
import { cn } from "@btnet/utilities-core";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { isNullOrWhitespace } from "utilities/string/string";

import { useFormatDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate.utilities";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { TextTruncate } from "commonComponents/utilities/TextTruncate/TextTruncate";

import "./EntityApprovalsViewer.less";

import { UserApprovalStatusTag } from "./UserApprovalsStatusTag";

interface IEntityApprovalsViewerPresentationalProps {
    users: ApprovalsUser[];
    canViewAgreement: boolean;
    onViewAgreement: () => void;
    className?: string;
    showEmptyState?: boolean;
    canSign?: boolean;
    onSign?: () => Promise<void>;
    activeUserSigningConfig?: {
        disabledForUser: boolean;
        disabledTooltip: React.ReactNode;
    };
}

export const EntityApprovalsViewerPresentational = ({
    users,
    canViewAgreement,
    onViewAgreement,
    className,
    showEmptyState,
    canSign,
    onSign,
    activeUserSigningConfig,
}: IEntityApprovalsViewerPresentationalProps) => {
    const formatDate = useFormatDate();
    const userInfo = useContext(UserInfoContext);

    // Sort users by status priority: Declined > Internally Approved > Signed > Others
    // Within each status group, prioritize users with reasons for action
    const sortedUsers = useMemo(() => {
        if (!users) return [];

        const getStatusPriority = (status: SignatureRequestUserStatus) => {
            switch (status) {
                case SignatureRequestUserStatus.Declined:
                    return 1;
                case SignatureRequestUserStatus.InternallyApproved:
                    return 2;
                case SignatureRequestUserStatus.Signed:
                    return 3;
                default:
                    return 4; // Unsigned, NotApplicable, and any other status
            }
        };

        const getRolePriority = (type?: BTLoginTypes) => {
            switch (type) {
                case BTLoginTypes.OWNER:
                case BTLoginTypes.CONTACT:
                    return 1;
                case BTLoginTypes.BUILDER:
                    return 2;
                case BTLoginTypes.SUBS:
                    return 3;
                default:
                    return 4;
            }
        };

        const hasReason = (user: ApprovalsUser) => !isNullOrWhitespace(user.reasonForAction);

        return [...users].sort((a, b) => {
            const priorityA = getStatusPriority(a.status);
            const priorityB = getStatusPriority(b.status);

            // First sort by status priority
            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            // Within same status, prioritize users with reasons
            const hasReasonA = hasReason(a) ? 0 : 1; // 0 = has reason (higher priority)
            const hasReasonB = hasReason(b) ? 0 : 1; // 1 = no reason (lower priority)

            if (hasReasonA !== hasReasonB) {
                return hasReasonA - hasReasonB;
            }

            const roleA = getRolePriority(a.loginType);
            const roleB = getRolePriority(b.loginType);

            if (roleA !== roleB) {
                return roleA - roleB;
            }

            return a.userName.localeCompare(b.userName);
        });
    }, [users]);

    const showDate = (status: SignatureRequestUserStatus) => {
        return !(
            status === SignatureRequestUserStatus.Unsigned ||
            status === SignatureRequestUserStatus.NotApplicable
        );
    };

    const getUserLoginTypeIcon = (type?: BTLoginTypes) => {
        switch (type) {
            case BTLoginTypes.BUILDER:
                return <IconInternalUsers size="md" />;
            case BTLoginTypes.OWNER:
            case BTLoginTypes.CONTACT:
                return <IconHouse size="md" />;
            case BTLoginTypes.SUBS:
                return <IconSubsVendors size="md" />;
            default:
                return;
        }
    };

    const hasUsers = useMemo(() => sortedUsers && sortedUsers.length > 0, [sortedUsers]);
    const isImpersonating =
        userInfo?.isImpersonatingBuilder || userInfo?.isBuilderImpersonatingOwner;

    return (
        <PageSection
            className={cn("EntityApprovalsViewer", className)}
            data-testid="entity-approvals-section"
            title="Approvals"
            extra={
                canViewAgreement && (
                    <BTLink
                        data-testid="entity-approvals-link"
                        onClick={onViewAgreement}
                        isUnderline
                    >
                        View agreement
                    </BTLink>
                )
            }
            removeBodyPadding
        >
            {hasUsers ? (
                <div className="EntityApprovalsViewer-users" data-testid="entity-approval-users">
                    {sortedUsers.map((user: ApprovalsUser) => (
                        <div
                            key={user.userId}
                            className="EntityApprovalsViewer-users-item"
                            data-testid={`entity-approval-user-${user.userId}`}
                        >
                            <div className="EntityApprovalsViewer-users-info">
                                {getUserLoginTypeIcon(user.loginType)}
                                <div className="EntityApprovalsViewer-users-leftColumn">
                                    <TextTruncate>{user.userName}</TextTruncate>
                                    {user.statusChangedDateUtc && showDate(user.status) && (
                                        <TextTruncate
                                            className="EntityApprovalsViewer-users-signedDate"
                                            data-testid={`entity-approval-user-${user.userId}-date-display`}
                                        >
                                            {formatDate(user.statusChangedDateUtc, {
                                                showDate: true,
                                                showTime: false,
                                                showDayOfWeek: false,
                                            })}
                                        </TextTruncate>
                                    )}
                                </div>
                                <div className="EntityApprovalsViewer-users-rightColumn">
                                    {canSign && onSign && user.userId === userInfo?.globalUserId ? (
                                        <div className="EntityApprovalsViewer-users-actions">
                                            <BTPopover
                                                content={activeUserSigningConfig?.disabledTooltip}
                                                widthType="unrestricted"
                                                disabled={!activeUserSigningConfig?.disabledForUser}
                                            >
                                                <BTButton
                                                    data-testid={`entity-approval-user-${user.userId}-sign-button`}
                                                    onClick={onSign}
                                                    disabled={
                                                        isImpersonating ||
                                                        (activeUserSigningConfig?.disabledForUser ??
                                                            false)
                                                    }
                                                >
                                                    Sign
                                                </BTButton>
                                            </BTPopover>
                                        </div>
                                    ) : (
                                        <UserApprovalStatusTag status={user.status} />
                                    )}
                                </div>
                            </div>
                            {!isNullOrWhitespace(user.reasonForAction) && (
                                <div
                                    className="EntityApprovalsViewer-users-reason"
                                    data-testid={`entity-approval-user-${user.userId}-reason`}
                                >
                                    {user.reasonForAction}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                showEmptyState && (
                    <div
                        className="EntityApprovalsViewer-emptyState"
                        data-testid="entity-approvals-empty-state"
                    >
                        <IconXCircle />
                        <div>No users are required to approve.</div>
                    </div>
                )
            )}
        </PageSection>
    );
};
